/**
 * Router - Gestionnaire de routage SPA
 * @class Router
 */
export class Router {
    /**
     * Crée une instance du Router
     * @param {string} containerId - ID du conteneur principal
     */
    constructor(containerId) {
        this._routes = new Map();
        this._currentRoute = null;
        this._currentView = null;
        this._container = document.getElementById(containerId);
        this._defaultRoute = '/map';
        this._onRouteChange = null;

        // Écoute les changements d'URL
        window.addEventListener('popstate', () => this._handleRouteChange());
    }

    /**
     * Définit la route par défaut
     * @param {string} route - Route par défaut
     */
    setDefault(route) {
        this._defaultRoute = route;
    }

    /**
     * Enregistre une route
     * @param {string} path - Chemin de la route
     * @param {Function} viewFactory - Factory pour créer la vue
     */
    register(path, viewFactory) {
        this._routes.set(path, viewFactory);
    }

    /**
     * Définit le callback de changement de route
     * @param {Function} callback - Callback
     */
    setOnRouteChange(callback) {
        this._onRouteChange = callback;
    }

    /**
     * Navigue vers une route
     * @param {string} path - Chemin de la route
     * @param {boolean} pushState - Ajouter à l'historique
     */
    navigate(path, pushState = true) {
        if (pushState) {
            window.history.pushState({ path }, '', `#${path}`);
        }
        this._handleRouteChange();
    }

    /**
     * Force le rafraîchissement de la vue courante
     */
    async refresh() {
        if (this._currentView && typeof this._currentView.destroy === 'function') {
            this._currentView.destroy();
        }
        
        // Retrouve la factory pour la route actuelle
        const path = this._currentRoute;
        let viewFactory = this._routes.get(path);
        if (!viewFactory) return;

        // Recrée la vue
        this._currentView = await viewFactory();
        
        // Rend la vue
        if (this._container && this._currentView) {
            this._container.innerHTML = '';
            if (typeof this._currentView.render === 'function') {
                await this._currentView.render(this._container);
            }
        }
    }

    /**
     * Gère le changement de route
     * @private
     */
    async _handleRouteChange() {
        const path = this._getPathFromHash();
        
        if (path === this._currentRoute) {
            return;
        }

        // Trouve la route correspondante
        let viewFactory = this._routes.get(path);
        
        // Route par défaut
        if (!viewFactory) {
            viewFactory = this._routes.get(this._defaultRoute) || this._routes.values().next().value;
        }

        // Détruit l'ancienne vue
        if (this._currentView && typeof this._currentView.destroy === 'function') {
            this._currentView.destroy();
        }

        // Crée la nouvelle vue
        this._currentRoute = path;
        this._currentView = await viewFactory();

        // Rend la vue
        if (this._container && this._currentView) {
            this._container.innerHTML = '';
            if (typeof this._currentView.render === 'function') {
                await this._currentView.render(this._container);
            }
        }

        // Callback
        if (this._onRouteChange) {
            this._onRouteChange(path);
        }
    }

    /**
     * Extrait le chemin du hash
     * @returns {string} Chemin
     * @private
     */
    _getPathFromHash() {
        const hash = window.location.hash.slice(1) || '/map';
        return hash.startsWith('/') ? hash : `/${hash}`;
    }

    /**
     * Démarre le routeur
     */
    start() {
        this._handleRouteChange();
    }

    /**
     * Retourne la route actuelle
     * @returns {string} Route actuelle
     */
    getCurrentRoute() {
        return this._currentRoute;
    }
}
